/**
 * BACFREE — Global Scroll Reveal
 * Fades + slides up each Shopify section as it scrolls up into the more
 * visually prominent part of the screen (not the instant its edge touches
 * the very bottom of the viewport) — no waiting on images, no queueing
 * behind other sections. Triggers once per section per page load.
 * Header/navigation is excluded so it stays visible instantly. The entire
 * footer (payment methods + copyright bar) is treated as one block.
 *
 * SPECIAL CASE 1: the "Under-Sink Water Filter / Countertop Water Dispenser"
 * collection-list section (id: collection_list_8e6wHk) is tall/stacked on
 * mobile (multiple cards in one column), so treating it as a single block
 * caused later cards to be marked visible the instant the first card
 * triggered, before the person had scrolled to them. That one section is
 * animated per-card instead — every other section on the site is
 * unaffected and still reveals as a single block.
 *
 * SPECIAL CASE 2: on each of the 12 collection (category) pages, the hero
 * banner image AND the cream/beige "Collection heading" bar underneath it
 * get a plain fade instead of the default fade+slide-up (tagged with a
 * "scroll-reveal--fade-only" class — see scroll-reveal.css). Unlike every
 * other section, these two are NOT routed through the scroll
 * IntersectionObserver: they sit at the very top of the page, so they are
 * already inside the viewport the instant the page loads. Observing them
 * the normal way meant the "visible" class was frequently applied before
 * the browser had painted the opacity:0 starting state even once — so the
 * fade either flashed by unnoticed or didn't visibly run at all. Instead,
 * fadeInOnLoad() below triggers their fade directly on page load
 * (decoupled from scroll/intersection timing entirely, with a double
 * requestAnimationFrame to guarantee the starting state is painted
 * first), so the fade-in is guaranteed to play the same way every visit.
 *   - The banner's section id is the same "hero_qqzddc" key across most
 *     of the 12 collection templates (each template renders on its own
 *     page, so the id is never duplicated on a single page load); two
 *     templates use their own distinct hero id instead.
 *   - The heading bar's section id is plain "section" on every one of
 *     the 12 collection templates — but that same id is also used by the
 *     blog article template for its (unrelated) main content block. To
 *     avoid affecting blog articles, the heading bar only gets the
 *     fade-only treatment when one of the collection hero banner ids is
 *     also present on the page, which is true only on the 12 collection
 *     templates.
 * Nothing else on the site is affected — the homepage banner is a
 * different section type ("slideshow") and keeps the default animation.
 *
 * Disabled inside the Theme Editor so merchants always see full content.
 */
(function () {
  'use strict';

  var REVEAL_CLASS = 'scroll-reveal';
  var VISIBLE_CLASS = 'scroll-reveal--visible';
  var FADE_ONLY_CLASS = 'scroll-reveal--fade-only';

  // Only this one section gets per-card reveal instead of whole-section reveal.
  var PER_CARD_SECTION_ID = 'shopify-section-collection_list_8e6wHk';
  var CARD_SELECTOR = '.resource-list__item';

  // Collection page hero banner sections: plain fade, no slide-up.
  // Covers all 12 collection templates (most share "hero_qqzddc"; two
  // templates — collection.indoor-water-filter-1 and
  // collection.outdoor-water-filter — use their own distinct hero id).
  var FADE_ONLY_HERO_SECTION_IDS = [
    'shopify-section-hero_qqzddc',
    'shopify-section-hero_QY7pkL',
    'shopify-section-hero_yADmxt'
  ];

  // The cream/beige "Collection heading" bar shares this same plain id
  // ("section") across all 12 collection templates. The blog article
  // template also names its main section "section", so this id alone
  // isn't unique to collection pages — see isFadeOnlyTarget() below.
  var COLLECTION_HEADING_SECTION_ID = 'shopify-section-section';

  var io = null;

  function getIO() {
    if (io) return io;
    if (!('IntersectionObserver' in window)) return null;
    io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add(VISIBLE_CLASS);
            io.unobserve(entry.target);
          }
        });
      },
      // The bottom 30% of the viewport is excluded from the intersection
      // check, so an element must scroll up into the top 70% of the screen
      // (a more visually prominent position) before it triggers — rather
      // than firing the instant its edge merely touches the bottom edge.
      { threshold: 0.01, rootMargin: '0px 0px -30% 0px' }
    );
    return io;
  }

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  // Mobile browsers (Safari, Chrome) resize the visible area as the
  // address bar collapses/expands while scrolling. window.innerHeight can
  // briefly report a taller value than what is actually on screen right
  // after page load. window.visualViewport.height reflects the true
  // currently-visible area and avoids that false positive.
  function getViewportHeight() {
    if (window.visualViewport && window.visualViewport.height) {
      return window.visualViewport.height;
    }
    return window.innerHeight;
  }

  // True only for the collection hero banner, or for the "section" id
  // when it's genuinely the collection heading bar (i.e. a collection
  // hero banner is also present on the page) — never for the blog
  // article template, which reuses the same "section" id for unrelated
  // content and has no collection hero banner alongside it.
  function isFadeOnlyTarget(el) {
    if (!el) return false;
    if (FADE_ONLY_HERO_SECTION_IDS.indexOf(el.id) !== -1) return true;
    if (el.id === COLLECTION_HEADING_SECTION_ID) {
      return FADE_ONLY_HERO_SECTION_IDS.some(function (heroId) {
        return !!document.getElementById(heroId);
      });
    }
    return false;
  }

  // Fades an above-the-fold element in on page load, independent of
  // scroll position. See "SPECIAL CASE 2" above for why the hero banner
  // and collection heading bar use this instead of the shared
  // IntersectionObserver path that every other section uses.
  function fadeInOnLoad(el) {
    if (!el || el.dataset.scrollRevealTagged === 'true') return;
    el.dataset.scrollRevealTagged = 'true';
    el.classList.add(REVEAL_CLASS, FADE_ONLY_CLASS);
    // Double rAF guarantees the browser paints the opacity:0 starting
    // state at least once before the "visible" class flips it to 1 —
    // without this, the two can land in the same paint and the fade
    // never visibly plays.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        el.classList.add(VISIBLE_CLASS);
      });
    });
  }

  // Tags one element (a whole section OR a single card) as a reveal target
  // and starts observing it. Used both for normal whole-section sections
  // and for each individual card inside the per-card special case.
  function observeTarget(el) {
    if (!el || el.dataset.scrollRevealTagged === 'true') return;
    el.dataset.scrollRevealTagged = 'true';

    var obs = getIO();
    if (!obs) return; // no IntersectionObserver support: leave content visible, no animation

    el.classList.add(REVEAL_CLASS);
    obs.observe(el);
  }

  function register(el) {
    if (!el || el.dataset.scrollRevealRegistered === 'true') return;
    if (el.closest('#header-group')) return; // keep header/nav instantly visible

    el.dataset.scrollRevealRegistered = 'true';

    if (isDesignMode()) return; // Theme Editor: show everything immediately, no animation

    if (el.id === PER_CARD_SECTION_ID) {
      var cards = el.querySelectorAll(CARD_SELECTOR);
      if (cards.length > 0) {
        cards.forEach(observeTarget);
        return;
      }
      // Fallback: markup changed and no cards were found — animate the
      // whole section as normal rather than showing nothing.
    }

    if (isFadeOnlyTarget(el)) {
      fadeInOnLoad(el);
      return;
    }

    observeTarget(el);
  }

  function scanRoot(root) {
    if (!root) return;
    root.querySelectorAll('.shopify-section').forEach(register);
  }

  // Safety net: catches reveal targets that never receive an intersection
  // callback because the shrunk (-30% bottom) observation area can never
  // be satisfied — e.g. content pinned at the true bottom of a short page
  // with no scroll room, or content already scrolled past (above the
  // viewport) if the page loaded on a mid-page anchor link. Deliberately
  // uses the FULL viewport height here (not shrunk), so it only ever
  // catches content that is already genuinely on-screen, never content
  // still below the fold that the person hasn't scrolled to yet.
  function sweepAlreadyVisible() {
    var viewportHeight = getViewportHeight();
    document
      .querySelectorAll('.' + REVEAL_CLASS + ':not(.' + VISIBLE_CLASS + ')')
      .forEach(function (el) {
        var rect = el.getBoundingClientRect();
        if (rect.top < viewportHeight) {
          el.classList.add(VISIBLE_CLASS);
          if (io) io.unobserve(el);
        }
      });
  }

  function init() {
    var main = document.querySelector('#MainContent');
    var footer = document.querySelector('footer');

    scanRoot(main);

    // The whole footer (payment methods row + copyright bar) reveals
    // together as a single block.
    if (footer) register(footer);

    if (typeof MutationObserver !== 'undefined' && main) {
      var mo = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          mutation.addedNodes.forEach(function (node) {
            if (node.nodeType !== 1) return;
            if (node.classList && node.classList.contains('shopify-section')) {
              register(node);
            }
            if (node.querySelectorAll) {
              node.querySelectorAll('.shopify-section').forEach(register);
            }
          });
        });
      });
      mo.observe(main, { childList: true, subtree: true });
    }

    window.addEventListener('load', sweepAlreadyVisible);
    window.addEventListener('resize', sweepAlreadyVisible);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', sweepAlreadyVisible);
    }
    setTimeout(sweepAlreadyVisible, 1200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-register a section after the Theme Editor re-renders it while a merchant is editing
  document.addEventListener('shopify:section:load', function (evt) {
    var section = evt.target && evt.target.closest ? evt.target.closest('.shopify-section') : null;
    if (section) {
      section.dataset.scrollRevealRegistered = '';
      register(section);
    }
  });
})();
